import React from 'react';
import { connect } from 'react-redux';
import io from 'socket.io-client';
import get from 'lodash/get';
import moment from 'moment';
import momenttz from 'moment-timezone';
import { setVisitorId } from '../redux/actions/meta';
import { getParams, getVisitorId } from '../util';
import host from '../util/host';
import LocalStorageServices from '../util/localStorageServices';
import SessionStorageServices from '../util/sessionStorageServices';

const STATUS = {
	CHECKING: 'checking',
	ALLOWED: 'allowed',
	BLOCKED: 'blocked',
};

class IdentityCheck extends React.PureComponent {
	constructor(props) {
		super(props);
		const { hasIdentityCheckEnable } = props;
		// Start in checking when enabled so app APIs wait for allow/block.
		this.state = {
			status: hasIdentityCheckEnable ? STATUS.CHECKING : STATUS.ALLOWED,
		};
		this.uid = '';
		this.visitorId = '';
		this.identityCheckInitiated = false;
		this._visitorResolutionComplete = false;
		this._identityStartEmittedForCurrentSocket = false;
		this._isMounted = false;
		this._checkTimeoutId = null;
		// null so the first status always notifies Main (needed when feature is off).
		this._lastStatus = null;
		this._connectSocket = this._connectSocket.bind(this);
		this._maybeStartIdentityCheck = this._maybeStartIdentityCheck.bind(this);
	}

	componentDidMount() {
		this._isMounted = true;
		if (!this.props.hasIdentityCheckEnable) {
			this._setStatus(STATUS.ALLOWED);
			return;
		}
		this._startCheckTimeout();
		this._resolveVisitorAndStart();
	}

	componentDidUpdate(prevProps) {
		const { visitorId, hasIdentityCheckEnable } = this.props;
		if (visitorId) {
			this.visitorId = visitorId;
		}

		if (prevProps.hasIdentityCheckEnable && !hasIdentityCheckEnable) {
			this.identityCheckInitiated = false;
			if (this.socket) {
				this.socket.removeAllListeners();
				this.socket.disconnect();
				this.socket = null;
			}
			this._setStatus(STATUS.ALLOWED);
			return;
		}

		// Feature turned on after mount — resolve visitor then start (or start if already resolved).
		if (!prevProps.hasIdentityCheckEnable && hasIdentityCheckEnable) {
			this.identityCheckInitiated = false;
			this._setStatus(STATUS.CHECKING);
			this._startCheckTimeout();
			if (this._visitorResolutionComplete) {
				this._maybeStartIdentityCheck();
			} else {
				this._resolveVisitorAndStart();
			}
			return;
		}

		if (hasIdentityCheckEnable) {
			this._maybeStartIdentityCheck();
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
		this._clearCheckTimeout();
		if (this.socket) {
			this.socket.removeAllListeners();
			this.socket.disconnect();
			this.socket = null;
		}
		this.identityCheckInitiated = false;
	}

	_clearCheckTimeout() {
		if (this._checkTimeoutId != null) {
			clearTimeout(this._checkTimeoutId);
			this._checkTimeoutId = null;
		}
	}

	_startCheckTimeout() {
		this._clearCheckTimeout();
		// Fail open if the server never answers so a WS outage cannot brick the app.
		this._checkTimeoutId = setTimeout(() => {
			this._checkTimeoutId = null;
			if (this._lastStatus !== STATUS.ALLOWED && this._lastStatus !== STATUS.BLOCKED) {
				this._setStatus(STATUS.ALLOWED);
			}
		}, this.props.identityCheckTimeoutMs);
	}

	async _resolveVisitorAndStart() {
		const { handleSetVisitorId } = this.props;

		// Browser-unique fingerprint, shared across tabs for reconnect matching.
		const existingVisitorId = LocalStorageServices.getVisitorId();
		if (!existingVisitorId) {
			const resolvedVisitorId = await getVisitorId();
			if (!this._isMounted) {
				return;
			}
			this.visitorId = resolvedVisitorId;
			handleSetVisitorId(this.visitorId);
			LocalStorageServices.setVisitorId(this.visitorId);
		} else {
			this.visitorId = existingVisitorId;
			handleSetVisitorId(existingVisitorId);
		}

		this._visitorResolutionComplete = true;
		this._maybeStartIdentityCheck();
	}

	// Notify Main once per status so initApp runs only after allow.
	_setStatus(status) {
		if (this._lastStatus === status) {
			return;
		}
		if (status !== STATUS.CHECKING) {
			this._clearCheckTimeout();
		}
		this._lastStatus = status;
		if (this._isMounted) {
			this.setState({ status });
		}
		const { onStatusChange } = this.props;
		if (onStatusChange) {
			onStatusChange(status);
		}
	}

	_maybeStartIdentityCheck() {
		if (!this._visitorResolutionComplete) {
			return;
		}
		if (!this.props.hasIdentityCheckEnable) {
			this._setStatus(STATUS.ALLOWED);
			return;
		}

		const lockKey = this._getLockKey();
		// No uid/session to lock — nothing to block, let the app start.
		if (!lockKey) {
			this._setStatus(STATUS.ALLOWED);
			return;
		}

		if (this.identityCheckInitiated) {
			return;
		}

		this.uid = lockKey;
		// Same tab was blocked earlier — skip socket and APIs.
		const identity = SessionStorageServices.getIdentity(this.uid);
		if (identity === 'invalid') {
			this._setStatus(STATUS.BLOCKED);
			return;
		}

		this._connectSocket();
		this._startCheckTimeout();
		this.identityCheckInitiated = true;
	}

	_getLockKey() {
		const { uid, sessionId, challengeId } = this._getIds();
		return uid || sessionId || challengeId || '';
	}

	_getIds() {
		const { location } = window;
		let uid = '';
		let sessionId = '';
		let challengeId = '';
		if (location.search.match(/uid/)) {
			uid = getParams('uid');
		}
		if (location.search.match(/session_id/)) {
			sessionId = getParams('session_id');
		}
		if (location.search.match(/challenge_id/)) {
			challengeId = getParams('challenge_id');
		}
		return { uid, sessionId, challengeId };
	}

	_getUserData() {
		return {
			sourcePage: window.location.pathname,
			sourceUrl: window.location.search,
			userTimeZone: moment().toString(),
			currentDateTime: momenttz.tz.guess(),
		};
	}

	_isMatchingUid(dataUid) {
		const lockKey = this._getLockKey();
		return dataUid === this.uid || dataUid === lockKey;
	}

	_connectSocket() {
		if (this.socket) {
			this.socket.removeAllListeners();
			this.socket.disconnect();
			this.socket = null;
		}

		const { WS_BASE_URL } = host();
		this._identityStartEmittedForCurrentSocket = false;

		this.socket = io.connect(WS_BASE_URL, {
			transports: ['websocket'],
			upgrade: false,
			query: {
				...this._getUserData(),
				uid: this.uid,
			},
			reconnection: true,
		});

		this.socket.on('connect', () => {
			const reconnect = this._identityStartEmittedForCurrentSocket;
			this._identityStartEmittedForCurrentSocket = true;
			const payload = {
				...this._getUserData(),
				uid: this.uid,
				visitorId: this.visitorId || navigator.userAgent,
			};
			if (reconnect) {
				payload.reconnect = true;
			}
			this.socket.emit('START_IDENTITY_CHECK', JSON.stringify(payload));
		});

		// This tab owns the session — safe to start app APIs.
		this.socket.on('IDENTITY_CHECK_ALLOWED', (data) => {
			if (this._isMatchingUid(data.uid)) {
				this._setStatus(STATUS.ALLOWED);
			}
		});

		// Another tab/browser already owns the session — block UI and APIs.
		this.socket.on('PING_INACTIVE_SESSION', (data) => {
			if (this._isMatchingUid(data.uid)) {
				this.socket.emit(
					'PONG_INACTIVE_SESSION',
					JSON.stringify({ uid: this.uid, ...this._getUserData() }),
				);
				SessionStorageServices.setIdentity(this.uid, 'invalid');
				this._setStatus(STATUS.BLOCKED);
			}
		});
	}

	render() {
		const { children } = this.props;
		const { status } = this.state;

		if (status === STATUS.BLOCKED) {
			return (
				<div style={styles.overlay}>
					<div style={styles.card}>
						<p style={styles.message}>
							This application is already open in another tab or browser.
							Please return to your original session.
						</p>
					</div>
				</div>
			);
		}

		// Hold children until allow so Layout does not mount early.
		if (status === STATUS.CHECKING) {
			return null;
		}

		return children;
	}
}

const styles = {
	overlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(255, 255, 255, 0.97)',
		zIndex: 9999,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	card: {
		maxWidth: '480px',
		padding: '40px 48px',
		borderRadius: '8px',
		boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
		backgroundColor: '#ffffff',
		textAlign: 'center',
	},
	message: {
		fontSize: '18px',
		color: '#424242',
		lineHeight: '1.6',
		margin: 0,
	},
};

const mapStateToProps = (state, props) => {
	const visitorId = get(state, 'meta.visitorId', '');
	const hasIdentityCheckEnable = get(props, 'identityCheckEnable', false);
	return {
		visitorId,
		hasIdentityCheckEnable,
	};
};

const mapDispatchToProps = (dispatch) => ({
	handleSetVisitorId: (visitorId) => dispatch(setVisitorId(visitorId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IdentityCheck);
